import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AppShell, type ViewKey } from './components/AppShell'
import { EMPTY_FILTERS, FilterBar, type FilterState } from './components/FilterBar'
import { RefreshButton } from './components/RefreshButton'
import { COMMON, OWNER_NAME, SOURCE_STATUS } from './labels'
import { api } from './lib/api'
import { todayIso } from './lib/format'
import type {
  Alert as AlertT,
  CycleView,
  Roadmap,
  SourceAvailability,
  SourceCell,
  Sprint,
  Summary,
} from './lib/types'
import { FONT_MONO, INK_FAINT } from './theme'
import { BacklogRgmView } from './views/BacklogRgmView'
import { CalendarView } from './views/CalendarView'
import { RefreshView } from './views/RefreshView'
import { PlanDocView } from './views/PlanDocView'
import { RoadmapView } from './views/RoadmapView'
import { SourceDrawer } from './views/SourceDrawer'
import { TodayView } from './views/TodayView'

interface Data {
  summary: Summary
  alerts: AlertT[]
  cycles: CycleView[]
  cells: SourceCell[]
  sources: SourceAvailability[]
  roadmap: Roadmap
}

export function App({ userSlot }: { userSlot?: React.ReactNode }) {
  // El callback de Notion regresa con `?view=rgm`: se abre directo ahí.
  const [view, setView] = useState<ViewKey>(() =>
    new URLSearchParams(window.location.search).get('view') === 'rgm' ? 'rgm' : 'today',
  )
  const [data, setData] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)
  const [selected, setSelected] = useState<SourceCell | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      // En paralelo: cada panel es independiente y esperarlos en serie sólo
      // haría más lenta la primera pintura.
      const [summary, alerts, cycles, calendar, roadmap] = await Promise.all([
        api.summary(),
        api.alerts(),
        api.cycles(),
        api.calendar(3, 6),
        api.roadmap(),
      ])
      setData({
        summary,
        alerts: alerts.alerts,
        cycles: cycles.cycles,
        cells: calendar.cells,
        sources: calendar.sources,
        roadmap,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : COMMON.error)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const today = data?.summary.today ?? todayIso()

  const calendarSprints = useMemo<Sprint[]>(() => {
    if (!data?.roadmap.available) return []
    const now = Date.parse(today)
    return data.roadmap.sprints.map((s) => {
      const a = Date.parse(s.start)
      const b = Date.parse(s.end)
      const progress = now <= a ? 0 : now >= b ? 1 : (now - a) / (b - a)
      return {
        id: s.id,
        name: s.id,
        label: `${s.id} · ${s.name}`,
        start_date: s.start,
        end_date: s.end,
        status: s.kind,
        progress,
        task_count: data.roadmap.tasks.filter((t) => t.sprint === s.id).length,
      }
    })
  }, [data, today])

  const filtered = useMemo(() => {
    if (!data) return []
    return data.cells.filter(
      (c) =>
        (!filters.source || c.source_id === filters.source) &&
        (!filters.status || c.status === filters.status) &&
        (!filters.period || c.period === filters.period),
    )
  }, [data, filters])

  const options = useMemo(() => {
    if (!data) return { sources: [], statuses: [], periods: [], cycles: [], roadmapOwners: [] }
    const uniq = <T,>(xs: T[]) => [...new Set(xs)]
    return {
      sources: uniq(data.cells.map((c) => c.source_id)).map((id) => ({
        value: id,
        label: data.cells.find((c) => c.source_id === id)!.source_name,
      })),
      statuses: uniq(data.cells.map((c) => c.status)).map((s) => ({
        value: s,
        label: SOURCE_STATUS[s] ?? s,
      })),
      periods: uniq(data.cells.map((c) => c.period))
        .sort()
        .map((p) => ({ value: p, label: p })),
      cycles: uniq(data.cycles.map((c) => c.cycle_id)).map((id) => ({
        value: id,
        label: data.cycles.find((c) => c.cycle_id === id)!.cycle_name,
      })),
      roadmapOwners: uniq(
        data.roadmap.tasks.map((t) => t.owner).filter((o): o is string => Boolean(o)),
      ).map((o) => ({ value: o, label: OWNER_NAME[o] ?? o })),
    }
  }, [data])

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!data) {
    return (
      // Una barra, no una rueda: sugiere avance en vez de espera indefinida.
      <Stack alignItems="center" sx={{ pt: 14 }} spacing={2.5}>
        <LinearProgress sx={{ width: 180, height: 4 }} />
        <Box
          component="span"
          sx={{
            fontFamily: FONT_MONO,
            fontSize: '0.6875rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: INK_FAINT,
          }}
        >
          {COMMON.loading}
        </Box>
      </Stack>
    )
  }

  const pricing = data.cycles.filter((c) => c.kind === 'pricing_refresh')
  const shown = filters.cycle
    ? data.cycles.filter((c) => c.cycle_id === filters.cycle)
    : data.cycles

  return (
    <AppShell
      view={view}
      onViewChange={setView}
      attentionCount={data.alerts.length}
      userSlot={
        <Stack direction="row" spacing={2} alignItems="center">
          <RefreshButton
            lastCheckedAt={data.summary.last_checked_at}
            today={today}
            onDone={() => void load()}
          />
          {userSlot}
        </Stack>
      }
    >
      {view !== 'today' && view !== 'backlog' && view !== 'rgm' && (
        <Box sx={{ mb: 3 }}>
          <FilterBar
            value={filters}
            onChange={setFilters}
            sources={view === 'calendar' ? options.sources : undefined}
            statuses={view === 'calendar' ? options.statuses : undefined}
            periods={view === 'calendar' ? options.periods : undefined}
            cycles={view === 'refresh' ? options.cycles : undefined}
            owners={view === 'roadmap' ? options.roadmapOwners : undefined}
          />
        </Box>
      )}

      {view === 'today' && (
        <TodayView summary={data.summary} alerts={data.alerts} cycles={data.cycles} />
      )}

      {view === 'calendar' && (
        <CalendarView
          cells={filtered}
          fichas={data.sources}
          cycles={data.cycles}
          sprints={calendarSprints}
          today={today}
          onSelect={setSelected}
        />
      )}

      {view === 'refresh' && (
        <Stack spacing={3}>
          {(shown.length ? shown : pricing).map((c) => (
            <RefreshView
              key={`${c.cycle_id}-${c.data_period}`}
              cycle={c}
              today={today}
              onChanged={() => void load()}
            />
          ))}
        </Stack>
      )}

      {view === 'roadmap' && (
        <RoadmapView
          roadmap={data.roadmap}
          today={today}
          ownerFilter={filters.owner}
          onChanged={() => void load()}
        />
      )}

      {view === 'backlog' && <PlanDocView roadmap={data.roadmap} />}

      {view === 'rgm' && <BacklogRgmView today={today} />}

      <SourceDrawer cell={selected} onClose={() => setSelected(null)} />
    </AppShell>
  )
}
